import { Injectable, OnDestroy, computed, signal, type Signal, type WritableSignal } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { SplitFactory } from '@splitsoftware/splitio/client';

import { $user } from '../../../../shared/constants/user';
import { $tenant } from '../../../../shared/constants/tenant';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { IUserServerResponse } from '../../../../shared/interfaces/user.interface';
import { ITenantInfoWithPackageInfo } from '../../../../shared/interfaces/tenant.interface';
import {
  FeatureFlagName,
  FME_ADMIN_TENANT_ID,
  FME_ANONYMOUS_KEY,
  FME_DEFAULT_FALLBACK_TREATMENT,
  FME_FALLBACK_TREATMENT_BY_FLAG,
} from '../../../../shared/interfaces/feature-flag.interface';
import { E2eFmeFlagMock } from '../../../../shared/constants/e2e-fme-flag.mock';
import { E2eFmeFlagOverrideMap } from '../../../../shared/interfaces/e2e-fme-flag-override.interface';
import { environment } from '../../../environments/environment';
import { EnvironmentType } from '../../shared/constants';
import { AppConfigService } from '../app-config.service';
import { UserService } from '../user.service';
import { E2eFmeFlagOverridesApi } from '../rest/e2e-fme-flag-overrides.api';

@Injectable({ providedIn: 'root' })
export class FeatureManagementService implements OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();
  private client: SplitIO.IBrowserClient | null = null;
  private tenantId: string | null = null;
  private isE2eMocked: boolean = false;
  private e2eOverrides: E2eFmeFlagOverrideMap = {};
  private readonly _ready: WritableSignal<boolean> = signal(false);
  private readonly updateTick: WritableSignal<number> = signal(0);

  /** True once the SDK has loaded flag definitions at least once. Flag
   *  signals from `getTreatment`/`isOn` update automatically on
   *  SDK_READY/SDK_UPDATE, so consumers don't need to gate on this to read
   *  correct values — it's for UI that wants to know readiness explicitly. */
  readonly ready: Signal<boolean> = this._ready.asReadonly();

  constructor(
    private appConfigService: AppConfigService,
    private userService: UserService,
    private e2eFmeFlagOverridesApi: E2eFmeFlagOverridesApi,
  ) {
    const userIsEqual = (userA: IUserServerResponse, userB: IUserServerResponse): boolean =>
      userA?.[$user._id] === userB?.[$user._id];

    this.subscriptions.add(
      this.userService.userDataChanged$
        .pipe(
          distinctUntilChanged(userIsEqual),
          switchMap(
            (user: IUserServerResponse): Observable<(IUserServerResponse | ITenantInfoWithPackageInfo)[]> =>
              this.userService.currentTenant$.pipe(
                map((tenant: ITenantInfoWithPackageInfo | null) => [user, tenant]),
                catchError(() => of([user, null])),
              ),
          ),
        )
        .subscribe(this.identify.bind(this)),
    );
  }

  private async identify([user, tenant]: [IUserServerResponse, ITenantInfoWithPackageInfo]): Promise<void> {
    await this.shutdown();

    const userId: string = SharedCommonUtility.notNullish(user) ? user[$user._id] : FME_ANONYMOUS_KEY;
    const tenantId: string | null = tenant?.[$tenant._id] ?? FME_ADMIN_TENANT_ID;

    this.initialize(userId, this.appConfigService.getHarnessFMEClientKey(), tenantId);
  }

  public initialize(userId: string, sdkKey: string, tenantId: string | null): void {
    if (SharedCommonUtility.notNullish(this.client) || this.isE2eMocked) {
      return;
    }
    this.tenantId = tenantId;

    // Mock the FME response when the client is running under the e2e environment, so e2e specs never
    // depend on the real Harness FME rollout state.
    if (environment.environmentType === EnvironmentType.QA) {
      this.isE2eMocked = true;
      this.loadE2eOverrides(tenantId);
      this._ready.set(true);
      this.updateTick.update((tick: number) => tick + 1);
      return;
    }

    const factory: SplitIO.IBrowserSDK = SplitFactory({
      core: {
        authorizationKey: sdkKey,
        key: userId,
      },
    });
    const client: SplitIO.IBrowserClient = factory.client();
    client.on(client.Event.SDK_READY, () => {
      this._ready.set(true);
      this.updateTick.update((tick: number) => tick + 1);
    });
    client.on(client.Event.SDK_READY_TIMED_OUT, () => {
      console.warn('[FeatureManagementService.initialize] FME client timed out waiting for SDK_READY');
    });
    client.on(client.Event.SDK_UPDATE, () => {
      this.updateTick.update((tick: number) => tick + 1);
    });
    this.client = client;
  }

  public getTreatment(featureFlagName: FeatureFlagName, attributes?: SplitIO.Attributes): Signal<SplitIO.Treatment> {
    return computed(() => {
      this.updateTick();
      if (this.isE2eMocked) {
        return this.resolveE2eTreatment(featureFlagName);
      }
      if (SharedCommonUtility.isNullish(this.client)) {
        return FME_FALLBACK_TREATMENT_BY_FLAG[featureFlagName] ?? FME_DEFAULT_FALLBACK_TREATMENT;
      }

      return this.client.getTreatment(featureFlagName, this.withTenantId(attributes));
    });
  }

  private resolveE2eTreatment(featureFlagName: FeatureFlagName): SplitIO.Treatment {
    const isOn: boolean | undefined = SharedCommonUtility.notNullish(this.e2eOverrides[featureFlagName])
      ? this.e2eOverrides[featureFlagName]
      : E2eFmeFlagMock[featureFlagName];
    if (SharedCommonUtility.notNullish(isOn)) {
      return isOn ? 'on' : 'off';
    }
    return FME_FALLBACK_TREATMENT_BY_FLAG[featureFlagName] ?? FME_DEFAULT_FALLBACK_TREATMENT;
  }

  private loadE2eOverrides(tenantId: string | null): void {
    this.subscriptions.add(
      this.e2eFmeFlagOverridesApi.getForCurrentUser(tenantId ?? undefined).subscribe({
        next: (response: { flags: E2eFmeFlagOverrideMap }) => {
          this.e2eOverrides = response?.flags ?? {};
          this.updateTick.update((tick: number) => tick + 1);
        },
        error: (err: unknown) => {
          console.warn('[FeatureManagementService.loadE2eOverrides] could not fetch overrides', err);
          this.e2eOverrides = {};
        },
      }),
    );
  }

  private withTenantId(attributes?: SplitIO.Attributes): SplitIO.Attributes {
    return {
      ...attributes,
      ...(SharedCommonUtility.notNullish(this.tenantId) ? { tenantId: this.tenantId } : {}),
    };
  }

  public isOn(featureFlagName: FeatureFlagName, attributes?: SplitIO.Attributes): Signal<boolean> {
    const treatment: Signal<SplitIO.Treatment> = this.getTreatment(featureFlagName, attributes);
    return computed(() => treatment() === 'on');
  }

  public async shutdown(): Promise<void> {
    if (this.isE2eMocked) {
      this.isE2eMocked = false;
      this.e2eOverrides = {};
      this.tenantId = null;
      this._ready.set(false);
      return;
    }
    if (SharedCommonUtility.isNullish(this.client)) {
      return;
    }
    const client: SplitIO.IBrowserClient = this.client;
    this.client = null;
    this.tenantId = null;
    this._ready.set(false);
    await client.destroy();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    void this.shutdown();
  }
}
